import { randomString } from 'remeda';

import { RewardModel, RewardModelWithChance, RewardModelWithoutId, VipWheelSettingsModel } from '@shared/types';
import { getChangedRewardsInfo, transformRewardsFromMapToObject, transformRewardsFromObjectToMap } from '@shared/utils';

import { VipWheelSettingsFormValues } from './VipWheelSettingsForm';

export const updateVipWheelRewards = async (
  initialRewards: RewardModelWithChance[],
  currentRewards: RewardModelWithChance[],
  createReward: (reward: RewardModel) => Promise<RewardModel>,
  updateReward: (payload: { rewardId: string; payload: RewardModelWithoutId }) => Promise<RewardModel>,
  bulkDeleteRewards: (rewardIds: string[]) => void
): Promise<Record<string, number>> => {
  const { deletedRewardIds, createdRewardItems, updatedRewardItems, restRewardItems } = getChangedRewardsInfo(
    initialRewards,
    currentRewards
  );

  const isDeletedRewardsExisting = deletedRewardIds.length !== 0;

  if (isDeletedRewardsExisting) {
    await bulkDeleteRewards(deletedRewardIds);
  }

  let createdRewards: RewardModelWithChance[] = [];
  let updatedRewards: RewardModelWithChance[] = [];

  const isCreatedRewardsExisting = createdRewardItems.length !== 0;

  if (isCreatedRewardsExisting) {
    createdRewards = await Promise.all(
      createdRewardItems.map(async (reward) => {
        const { id, type, value } = await createReward(reward);

        return new RewardModelWithChance(id, type, value, reward.chance);
      })
    );
  }

  const isUpdatedRewardsExisting = updatedRewardItems.length !== 0;

  if (isUpdatedRewardsExisting) {
    updatedRewards = await Promise.all(
      updatedRewardItems.map(async ({ id, chance, ...rewardParams }) => {
        const { type, value } = await updateReward({
          rewardId: id,
          payload: rewardParams,
        });

        return new RewardModelWithChance(id, type, value, chance);
      })
    );
  }

  return transformRewardsFromMapToObject([...createdRewards, ...updatedRewards, ...restRewardItems]);
};

export const getEditFormInitialValues = (
  vipWheelSettings: VipWheelSettingsModel,
  initialDefaultRewards: RewardModel[],
  initialDiamondRewards: RewardModel[],
  initialEliteRewards: RewardModel[]
): VipWheelSettingsFormValues => {
  const {
    id,
    defaultRewardsMap,
    diamondRewardsMap,
    eliteRewardsMap,
    defaultCryptocheckLinks = [],
    diamondCryptocheckLinks = [],
    eliteCryptocheckLinks = [],
    ...restDetails
  } = vipWheelSettings;

  return {
    ...restDetails,
    defaultCryptocheckLinks: defaultCryptocheckLinks.map((value) => ({
      id: randomString(5),
      value,
    })),
    diamondCryptocheckLinks: diamondCryptocheckLinks.map((value) => ({
      id: randomString(5),
      value,
    })),
    eliteCryptocheckLinks: eliteCryptocheckLinks.map((value) => ({
      id: randomString(5),
      value,
    })),
    defaultRewardsMap: transformRewardsFromObjectToMap(defaultRewardsMap, initialDefaultRewards),
    diamondRewardsMap: transformRewardsFromObjectToMap(diamondRewardsMap, initialDiamondRewards),
    eliteRewardsMap: transformRewardsFromObjectToMap(eliteRewardsMap, initialEliteRewards),
  };
};
